import { Button, DatePicker, Empty, Modal, Radio, Select } from '@maxtropy/components';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import dayjs, { Dayjs } from 'dayjs';
import { ShareDataContext } from '@/pages/IndustrialGreenMicBigScreen';
import {
  apiV2MicrogridOverviewGetEarliestTimeOfReasoningPost,
  apiV2MicrogridOverviewGetPowerPredictPost,
  apiV2MicrogridResolvedGetStrategyObjectListPost,
} from '@maxtropy/intelligent-dispath-apis-v2';
import { useRequest } from 'ahooks';
import { isNil } from 'lodash-es';
import { Spin } from 'antd';
import { getModalChartOption } from './chart';
import ReactEcharts from 'echarts-for-react';

export enum StationType {
  PV = 3,
  WP = 2,
  LOAD = 0,
}

export const StationTypeDisplay = {
  [StationType.PV]: '光伏站',
  [StationType.WP]: '风电站',
  [StationType.LOAD]: '负载',
};

export interface IPowerPredictionModal {
  visible: boolean;
  onCancel: () => void;
}

const PowerPredictionModal: FC<IPowerPredictionModal> = props => {
  const { visible, onCancel } = props;
  const [displayValue, setDisplayValue] = useState<Dayjs>(dayjs().startOf('day'));
  const [radioValue, setRadioValue] = useState<number>(3);
  const [stationId, setStationId] = useState<number>();

  const { microgridId, dataExit } = useContext(ShareDataContext);

  // 并网点
  const { data: deviceData } = useRequest(
    async () => {
      const res = await apiV2MicrogridResolvedGetStrategyObjectListPost({
        configId: microgridId,
        objectType: [radioValue],
        transform: false,
      });
      return res.list ?? [];
    },
    {
      ready: !!microgridId && !isNil(radioValue) && visible,
      refreshDeps: [microgridId, radioValue, visible],
    }
  );

  // 默认选择第一个设备
  useEffect(() => {
    if (!deviceData || deviceData?.length === 0) return;
    setStationId(deviceData?.[0].objectId);
  }, [deviceData]);

  // 图表数据
  const { data: chartData, loading } = useRequest(
    () => {
      return apiV2MicrogridOverviewGetPowerPredictPost({
        configId: microgridId,
        startDate: displayValue.valueOf(),
        objectType: [radioValue],
        stationId: stationId,
      });
    },
    {
      ready: !!microgridId && !!displayValue && !!stationId && !isNil(radioValue) && visible,
      refreshDeps: [microgridId, displayValue, stationId, radioValue, visible],
    }
  );

  // 查询图表
  const chartOption = useMemo(() => getModalChartOption(chartData), [chartData]);

  // 获取最早推理时间
  const { data: lastTime, loading: timeLoading } = useRequest(
    async () => {
      return apiV2MicrogridOverviewGetEarliestTimeOfReasoningPost({
        configId: microgridId,
      });
    },
    {
      ready: !!microgridId && visible,
      refreshDeps: [microgridId, visible],
    }
  );

  const disabledDate = (current: Dayjs) => {
    // 起始允许选择的日期（可根据业务设置）
    if (lastTime?.earliestTime) {
      const minDate = dayjs(lastTime?.earliestTime);
      const maxDate = dayjs().add(1, 'day').endOf('day'); // 当前日期+1天
      return current.isBefore(minDate, 'day') || current.isAfter(maxDate, 'day');
    } else {
      const maxDate = dayjs().add(1, 'day').endOf('day'); // 当前日期+1天
      return current.isAfter(maxDate, 'day');
    }
  };

  // 判断是否都为空，如果都为空则显示empty
  const isShowEmpty = useMemo(() => {
    if (chartData?.list?.length) {
      return false;
    }
    return true;
  }, [chartData]);

  return (
    <Modal
      // contentClassName="modal-form-content"
      size="big"
      destroyOnClose
      open={visible}
      onCancel={onCancel}
      getContainer={() => document.getElementById('full_screen_box') ?? document.body}
      title="功率预测"
      footer={<Button onClick={onCancel}>关闭</Button>}
    >
      <div className={styles.filter_box}>
        <div className={styles.time_select}>
          <span style={{ color: 'var(--mx-text-secondary-color)' }}>预测时间：</span>
          <DatePicker
            style={{ width: 220 }}
            disabledDate={disabledDate}
            allowClear={false}
            onChange={e => {
              setDisplayValue(dayjs(e).startOf('day'));
            }}
            value={displayValue}
            getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
          />
        </div>
        <div className={styles.radioBtn_box}>
          <Radio.Group
            buttonStyle="solid"
            value={radioValue}
            onChange={e => {
              setStationId(undefined);
              setRadioValue(e.target.value);
            }}
          >
            <Radio.Button value={3}>光伏</Radio.Button>
            {dataExit?.wpExists && <Radio.Button value={2}>风电</Radio.Button>}
            <Radio.Button value={0}>负载</Radio.Button>
          </Radio.Group>
        </div>
        <div className={styles.selectDevice_box}>
          <span style={{ color: 'var(--mx-text-secondary-color)' }}>
            {StationTypeDisplay[radioValue as StationType]}：
          </span>
          <Select
            style={{ width: 220 }}
            optionFilterProp="label"
            showSearch
            placeholder="请选择"
            value={stationId}
            onChange={v => setStationId(v)}
            options={(deviceData ?? []).map(item => ({
              label: item.objectName,
              value: item.objectId,
            }))}
          />
        </div>
      </div>
      <Spin spinning={timeLoading}>
        {dayjs(lastTime?.earliestTime).isAfter(dayjs()) || isShowEmpty ? (
          <Empty className={styles.modal_empty_box} />
        ) : (
          <Spin spinning={loading}>
            <ReactEcharts style={{ width: 960, height: 450 }} option={chartOption} notMerge lazyUpdate={false} />
          </Spin>
        )}
      </Spin>
    </Modal>
  );
};

export default PowerPredictionModal;
